/**
 * <FilterModule />
 *
 * Filter module filter content. Each field is a <CustomSelect /> so the
 * UX is consistent and accessible across all filter options.
 *
 *
 * UX contract:
 *   - Pending (local) state until "Apply" is clicked — no URL writes on every
 *     selection change.
 *   - Apply converts pending strings to properly-typed FilterValues and calls
 *     onApply(state) which writes to the URL.
 */

import { useEffect, useState } from 'react'
import CustomSelect from '@/components/ui/CustomSelect'
import Input from '@/components/ui/Input/Input'
import type { FilterSection, FilterState, FilterValue } from '@/types/filter'
import FilterButton from '@/components/ui/FilterButton'
import Calendar from '@/components/ui/Calendar/Calendar'
import { CalendarIcon } from 'lucide-react'
import { Paragraph } from '@/components/ui/Typography'
import formatDate from '@/utils/helpers/formatDate'

interface FilterModuleProps {
  sections: FilterSection[]
  activeFilters: FilterState
  onApply: (state: FilterState) => void
  activeFilterCount?: number
}

// Pending state within the panel is always string | null per field — the
// CustomSelect component works with string values and we type-convert on Apply.
type PendingState = Record<string, string | null>

/** Convert committed typed FilterState → string-based pending state for the selects */
function toPendingState(sections: FilterSection[], activeFilters: FilterState): PendingState {
  const pending: PendingState = {}
  sections.forEach(s => {
    const val = activeFilters[s.key]
    if (val === null || val === undefined) {
      pending[s.key] = null
    } else {
      // Stringify booleans/numbers for select value prop
      pending[s.key] = String(val)
    }
  })
  return pending
}

/** Convert string-based pending state → properly typed FilterState for the hook */
function toFilterState(sections: FilterSection[], pending: PendingState): FilterState {
  const state: FilterState = {}
  sections.forEach(s => {
    const raw = pending[s.key]
    const type = s.type ?? 'select'

    if (raw === null || raw === undefined || raw === '') {
      state[s.key] = null
      return
    }

    let typed: FilterValue
    switch (type) {
      case 'boolean':
        typed = raw === 'true'
        break
      case 'number':
        typed = Number(raw)
        break
      default:
        typed = raw
    }
    state[s.key] = typed
  })
  return state
}

const FilterModule = ({ sections, activeFilters, onApply, activeFilterCount }: FilterModuleProps) => {
  const [pending, setPending] = useState<PendingState>(() => toPendingState(sections, activeFilters))
  const [errors, setErrors] = useState<Record<string, string>>({});


  useEffect(() => {
    setPending(toPendingState(sections, activeFilters))
  }, [sections, activeFilters])

  // ── Field change ───────────────────────────────────────────────────────────

  const setField = (key: string, value: string | null) => {
    setPending(prev => ({ ...prev, [key]: value }))
  }

  // ── Apply ──────────────────────────────────────────────────────────────────

  const handleApply = () => {
    onApply(toFilterState(sections, pending))
  }

  // ── Render a field based on its type ──────────────────────────────────────

  const renderField = (section: FilterSection) => {
    const type = section.type ?? 'select'
    const value = pending[section.key] ?? ""

    if (type === 'boolean') {
      return (
        <CustomSelect
          options={section.options ?? [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' }]}
          value={value}
          onChange={value => setField(section.key, value as string | null)}
          placeholder={section.placeholder ?? `${section.label}…`}
          aria-label={section.label}
          isClearable={true}
        />
      )
    }

    if (type === 'select' && section.options) {
      return (
        <CustomSelect
          options={section.options}
          value={value}
          onChange={value => setField(section.key, value as string | null)}
          placeholder={section.placeholder ?? `${section.label}…`}
          aria-label={section.label}
          isClearable={true}
          isSearchable={true}
        />
      )
    }

    if (type === 'text') {
      return (
        <Input
          value={value ?? ''}
         onChange={value => setField(section.key, value)}
          placeholder={section.placeholder ?? `Enter ${section.label}…`}
          aria-label={section.label}
          $marginbottom={'0'}
        />
      )
    }

        if (type === "date" || type === "datetime" || type === "dateRange" || type === "dateTimeRange") {
      return (
        <div>
          <Calendar
            value={value ?? undefined}
            onChange={(date: Date | string) => {
              setField(section.key, date ? formatDate(date, "YYYY-MM-DD") : null);
              if (date) {
                setErrors((prev) => ({ ...prev, [section.key]: "" }));
              } else if (section.required) {
                setErrors((prev) => ({ ...prev, [section.key]: "This field is required" }));
              }
            }}
            placeholder={section.placeholder ?? `Select ${section.label}`}
            aria-label={section.label}
            icon={<CalendarIcon />}
            $marginbottom={"0"}
            selectedDate={value ? new Date(value) : undefined}
            $isClearable={true}
            showTimeSelect={type === "datetime" || type === "dateTimeRange"}
            required={section.required}
            
          />
          {errors[section.key] && <Paragraph className='!text-xs text-red-500'>{errors[section.key]}</Paragraph>}
        </div>
      );
    }

    // Fallback for future field types — renders nothing for now
    return null
  }

  return (
        <FilterButton activeCount={activeFilterCount}>
                {({ closeModule }) => (
                     <div role="dialog" aria-label="Event filters" className="w-72 bg-white rounded-lg shadow  border border-gray-200"
                      
                     >
      <div className="px-4 py-4 flex flex-col gap-4">
        {sections.map(section => (
          <div key={section.key} className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {section.label}
            </label>
            {renderField(section)}
          </div>
        ))}
      </div>

      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg">
        <button
          type="button"
          onClick={()=>{
            handleApply()
            closeModule()
          }}
          className="w-full py-2 bg-wine-100 text-white text-sm font-semibold font-satoshi rounded hover:bg-wine-200 transition-colors"
        >
          Apply
        </button>
      </div>
    </div>

                )}
              </FilterButton> 

  )
}

export default FilterModule
